import moment from '@lib/moment';
import { NILL } from '@lib/constants';
import { fullDateFormat } from '@lib/utils';

/**
 * Retrieve the appropriate lesson name, accounting for custom curricula and custom booth topics.
 *
 * @param session The session, which supplies the event context (event type and curriculum).
 * @param record The record holding the lesson and custom name. Defaults to the session itself;
 *   pass a `completed` record to get the delivered name rather than the planned one.
 * @return {string}
 */
export function getLessonName(session, record = session) {
  if (!session || !record) {
    return '';
  }

  // Custom curriculum (classes only): the lesson name is entered in session.session_name.
  if (session.event?.curriculum?.custom) {
    // CompletedSession has lesson_name. Session has session_name
    return record.lesson_name || session.session_name || '';
  }

  switch (session.event?.event_type) {
    case 'BOOTH':
      if (record.lesson?.custom) {
        return record.booth_topic_name || '';
      }
      return record.lesson?.booth_topic || '';
    case 'CLASS':
      return record.lesson?.lesson_name || '';
    default:
      return '';
  }
}

/**
 * Helper to get a user-friendly name for a session
 */
export const getSessionName = ({ session, dateFallback = false, useNill = true }) => {
  if (!session) {
    return useNill ? NILL : '';
  }

  // Custom curriculum - session.lesson will be null. The lesson name is entered in the session.session_name field.
  if (session.event?.curriculum?.custom && session.session_name) {
    return session.session_name;
  } else if (session.lesson?.custom && session.booth_topic_name) {
    return session.booth_topic_name;
  }

  let name = session.lesson?.lesson_name || session.lesson?.booth_topic;

  // The 'session_name' field is used to add a custom label to the end of the lesson name. (Classes only)
  if (session.session_name && session.event.event_type === 'CLASS') {
    name = `${name} - ${session.session_name}`
  }
  if (!name && dateFallback && session.mStart) {
    name = session.mStart.format('MMM D, YYYY | h:mm A');
  }
  if (!name) {
    if (useNill) {
      name = NILL;
    } else {
      name = session.id;
    }
  }
  return name;
};

/**
 * Gets the session numbers in "(number / total)" format, e.g., "(1 / 3)"
 * @param session
 * @return {string}
 */
export function getSessionNumbers(session) {
  if (session.sessionOrder) {
    return `(${session.sessionOrder.order || '-'} / ${session.sessionOrder.totalNonCanceled || '-'})`;
  }
  return '';
}

/**
 * Helper to get the session order and number of sessions in the event (e.g., "Session 3 / 4")
 */
export const getSessionOrder = (session) => {
  const values = {
    totalNonCanceled: null,
    order: null,
    isFirstIncompleteSession: false,
  }

  if (session && session.event.sessions) {
    // Calculate session order
    const allSessions = session.event.sessions;
    const nonCanceled = orderSessions(allSessions.filter((s) => s.status !== 'CANCELED'));
    values.totalNonCanceled = nonCanceled.length;

    if (session.status !== 'CANCELED') {
      values.order = nonCanceled.findIndex((os) => os.id === session.id) + 1;
    }

    // Is this the first incomplete session?
    if (session.status === 'INCOMPLETE' || !session.status) {
      const incomplete = orderSessions(allSessions.filter((s) => s.status === 'INCOMPLETE' || !s.status));
      values.isFirstIncompleteSession = incomplete.findIndex((s) => s.id === session.id) === 0;
    }
  }
  return values;
};

export const orderSessions = (sessions) => {
  sessions.sort((a, b) => {
    const aStart = moment(a.start);
    const bStart = moment(b.start);
    return aStart - bStart;
  });
  return sessions;
};

export const formatSessionDates = (start, end) => {
  const mStart = moment(start);
  const mEnd = moment(end);
  const diff = mEnd.diff(mStart, 'minutes');
  if (diff > ((24*60) - 1)) {
    return `${mStart.format('MM/DD/YYYY h:mm A')} - ${mEnd.format('MM/DD/YYYY h:mm A')}`;
  }
  return `${mStart.format('MM/DD/YYYY h:mm A')} - ${mEnd.format('h:mm A')}`;
};

export const formatSessionStart = (session) => {
  const mStart = moment(session.start);
  return mStart.format('MM/DD/YYYY h:mm A');
};

export function getSessionShortDate(session) {
  const mStart = moment(session.start);
  return mStart.format("M/D/YY");
}

export const getDateHeader = (session) => {
  if (!session) {
    return '';
  }
  return `${fullDateFormat(session.mStart)} - ${session.multiday ? fullDateFormat(session.mEnd) : session.mEnd.format('h:mm A')}`;
};

export const getDeliveryInfo = (session) => {
  const methods = session?.delivery_method ?? [];
  const site = session?.delivery_location;
  const info = []

  if (methods.includes('Virtual/Live Online')) {
    info.push('Virtual/Live Online');
  }
  if (site) {
    info.push(site);
  }

  return info;
};

export const isYouth = (session) => {
  if (!session) {
    return false;
  }
  if (session.event.event_type === 'BOOTH') {
    return false;
  }
  if (session.event.event_type === 'CLASS') {
    return session.lesson?.audience === 'Youth';
  }
  return false;
}

export const participantAttendedSession = (participant, session) => {
  if (!participant || !session) {
    return false;
  }
  // console.log('participantAttendedSession', participant, session.id);
  const attendanceRecord = participant.participantAttendance.find(attendance => attendance.session_id === session.id);
  // console.log('attendanceRecord', attendanceRecord, attendanceRecord?.status);
  if (!attendanceRecord) {
    return null;
  }
  return attendanceRecord?.status === 'PRESENT';
}

export function getParticipantName(participant) {
  return participant.middle_name
    ? `${participant.first_name} ${participant.middle_name} ${participant.last_name}`
    : `${participant.first_name} ${participant.last_name}`;
}

/**
 * Reusable sort function to sort participants by name alphabetically.
 *
 * Example:
 * const { data: participants } = useSessionParticipantsQuery(event.id);
 * participants.sort(sortByParticipantName)
 */
export function sortByParticipantName(a, b) {
  const aName = getParticipantName(a).toLowerCase();
  const bName = getParticipantName(b).toLowerCase();
  if (aName < bName) {
    return -1;
  }
  if (aName > bName) {
    return 1;
  }

  // names must be equal
  return 0;
}
